import type { GameEvent, StageDefinition, Vec2 } from './types';
import type { PhysicsSnapshot } from './physics';
import type { ViewportService } from './viewport';

interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

interface Ring { x: number; y: number; age: number; duration: number; color: string }
interface Stamp { x: number; y: number; age: number; text: string; color: string }

export interface AimVisual {
  active: boolean;
  vector: Vec2;
  strength: number;
}

const TAU = Math.PI * 2;

export class GardenRenderer {
  private readonly context: CanvasRenderingContext2D;
  private stage: StageDefinition;
  private readonly particles: Particle[] = Array.from({ length: 160 }, () => ({ active: false, x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, size: .05, color: '#d9694f' }));
  private rings: Ring[] = [];
  private stamps: Stamp[] = [];
  private trail: Vec2[] = [];
  private shake = 0;
  private time = 0;
  private transition = 1;
  private reduced = false;

  constructor(canvas: HTMLCanvasElement, private readonly viewport: ViewportService, stage: StageDefinition) {
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas 2D is unavailable.');
    this.context = context;
    this.stage = stage;
    this.applyPalette();
  }

  setStage(stage: StageDefinition): void {
    this.stage = stage;
    this.viewport.setWorld(stage.world.width, stage.world.height);
    this.trail.length = 0;
    this.rings.length = 0;
    this.stamps.length = 0;
    this.transition = 0;
    this.applyPalette();
  }

  setReducedEffects(reduced: boolean): void { this.reduced = reduced; }

  resetTrail(): void { this.trail.length = 0; }

  onEvent(event: GameEvent): void {
    switch (event.type) {
      case 'launch':
        this.burst(event.position, this.stage.palette.ink, 9, 3.5);
        this.rings.push({ ...event.position, age: 0, duration: .48, color: this.stage.palette.ink });
        break;
      case 'pulse':
        this.burst(event.position, this.stage.palette.coral, 18, 5.2);
        this.rings.push({ ...event.position, age: 0, duration: .55, color: this.stage.palette.coral });
        this.shake = Math.max(this.shake, .1);
        break;
      case 'petal':
        this.burst(event.position, this.stage.palette.sun, 20 + event.combo * 2, 4.4);
        this.stamps.push({ ...event.position, age: 0, text: `+${event.points}`, color: this.stage.palette.ink });
        this.shake = Math.max(this.shake, .045);
        break;
      case 'bumper':
        this.burst(event.position, this.stage.palette.coral, 14, 5.6 * event.intensity);
        this.rings.push({ ...event.position, age: 0, duration: .46, color: this.stage.palette.coral });
        this.shake = Math.max(this.shake, .13 * event.intensity);
        break;
      case 'surface':
        this.rings.push({ ...event.position, age: 0, duration: .28, color: this.stage.palette.ink });
        this.shake = Math.max(this.shake, .035 * event.intensity);
        break;
      case 'goal-rejected':
        this.rings.push({ ...event.position, age: 0, duration: .7, color: this.stage.palette.coral });
        this.stamps.push({ ...event.position, age: 0, text: 'TOO FAST', color: this.stage.palette.coral });
        this.shake = Math.max(this.shake, .16);
        break;
      case 'goal-locked':
        this.rings.push({ ...event.position, age: 0, duration: .62, color: this.stage.palette.sun });
        this.stamps.push({ ...event.position, age: 0, text: `${event.remaining} PETAL${event.remaining === 1 ? '' : 'S'} LEFT`, color: this.stage.palette.ink });
        this.shake = Math.max(this.shake, .1);
        break;
      case 'complete':
        this.burst(event.position, this.stage.palette.sun, 48, 6);
        this.rings.push({ ...event.position, age: 0, duration: 1.1, color: this.stage.palette.leaf });
        this.shake = Math.max(this.shake, .08);
        break;
      case 'fail':
        this.burst(event.position, this.stage.palette.coral, 25, 4.7);
        this.shake = Math.max(this.shake, .2);
        break;
    }
    this.rings = this.rings.slice(-12);
    this.stamps = this.stamps.slice(-10);
  }

  render(snapshot: PhysicsSnapshot, aim: AimVisual, delta: number, playing: boolean): void {
    this.time += Math.min(.05, delta);
    this.transition = Math.min(1, this.transition + delta * 1.4);
    this.updateEffects(delta);
    if (playing) this.updateTrail(snapshot.seed.position);

    const { dpr, cssWidth, cssHeight, scale, offset } = this.viewport.state;
    const ctx = this.context;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = this.stage.palette.paper;
    ctx.fillRect(0, 0, cssWidth, cssHeight);
    ctx.save();
    const shakeX = this.reduced ? 0 : Math.sin(this.time * 43) * this.shake * scale;
    const shakeY = this.reduced ? 0 : Math.cos(this.time * 37) * this.shake * scale * .6;
    ctx.translate(offset.x + shakeX, offset.y + shakeY);
    ctx.scale(scale, scale);
    ctx.globalAlpha = easeOutCubic(this.transition);
    this.drawField(ctx);
    this.drawWind(ctx);
    this.drawBotanicals(ctx);
    this.drawPlatforms(ctx, snapshot);
    this.drawTrail(ctx);
    this.drawGoal(ctx);
    this.drawPetals(ctx, snapshot.collectedPetals);
    this.drawBumpers(ctx);
    this.drawThorns(ctx);
    this.drawEffects(ctx);
    if (aim.active) this.drawAim(ctx, snapshot.seed.position, aim);
    this.drawSeed(ctx, snapshot.seed.position, snapshot.seed.rotation);
    ctx.restore();
    this.drawRegistration(ctx, cssWidth, cssHeight, dpr);
  }

  private drawField(ctx: CanvasRenderingContext2D): void {
    const { width, height } = this.stage.world;
    ctx.save();
    ctx.strokeStyle = withAlpha(this.stage.palette.ink, .065);
    ctx.lineWidth = .012;
    for (let x = 0; x <= width; x += 1) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
    }
    for (let y = 0; y <= height; y += 1) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }
    ctx.strokeStyle = withAlpha(this.stage.palette.ink, .15);
    ctx.setLineDash([.06, .13]);
    ctx.strokeRect(.18, .18, width - .36, height - .36);
    ctx.restore();
  }

  private drawBotanicals(ctx: CanvasRenderingContext2D): void {
    const { width, height } = this.stage.world;
    ctx.save();
    ctx.strokeStyle = withAlpha(this.stage.palette.leafDark, .24);
    ctx.fillStyle = withAlpha(this.stage.palette.leaf, .095);
    ctx.lineWidth = .035;
    const count = this.reduced ? 8 : 15;
    for (let i = 0; i < count; i += 1) {
      const x = ((i * 4.73 + this.stage.number * 1.9) % width);
      const stemHeight = 1.2 + ((i * 17) % 19) / 10;
      const lean = Math.sin(i * 8.2 + this.stage.number) * .32;
      const sway = this.reduced ? 0 : Math.sin(this.time * .7 + i) * .04;
      ctx.beginPath();
      ctx.moveTo(x, height);
      ctx.bezierCurveTo(x + lean, height - stemHeight * .35, x - lean * .2, height - stemHeight * .7, x + sway, height - stemHeight);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(x + lean * .25, height - stemHeight * .53, .34, .1, lean > 0 ? -.35 : .35, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawWind(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    for (const wind of this.stage.winds) {
      ctx.fillStyle = withAlpha(this.stage.palette.leaf, .055);
      ctx.fillRect(wind.x - wind.width / 2, wind.y - wind.height / 2, wind.width, wind.height);
      ctx.strokeStyle = withAlpha(this.stage.palette.leafDark, .28);
      ctx.lineWidth = .022;
      const direction = Math.atan2(wind.force.y, wind.force.x);
      for (let row = 0; row < 4; row += 1) {
        const y = wind.y - wind.height * .34 + row * wind.height * .22;
        const drift = this.reduced ? 0 : (this.time * .75 + row * .47) % .8;
        for (let column = 0; column < 4; column += 1) {
          const x = wind.x - wind.width * .39 + column * wind.width * .25 + drift;
          ctx.save(); ctx.translate(x, y); ctx.rotate(direction);
          ctx.beginPath(); ctx.moveTo(-.22, 0); ctx.lineTo(.22, 0); ctx.lineTo(.13, -.07); ctx.moveTo(.22, 0); ctx.lineTo(.13, .07); ctx.stroke();
          ctx.restore();
        }
      }
    }
    ctx.restore();
  }

  private drawPlatforms(ctx: CanvasRenderingContext2D, snapshot: PhysicsSnapshot): void {
    const draw = (x: number, y: number, width: number, height: number, angle = 0, moving = false): void => {
      ctx.save(); ctx.translate(x, y); ctx.rotate(angle);
      ctx.fillStyle = moving ? this.stage.palette.leaf : this.stage.palette.ink;
      ctx.fillRect(-width / 2, -height / 2, width, height);
      ctx.strokeStyle = withAlpha(this.stage.palette.paper, .28);
      ctx.lineWidth = .018;
      ctx.beginPath(); ctx.moveTo(-width / 2 + .1, -height / 2 + .06); ctx.lineTo(width / 2 - .1, -height / 2 + .06); ctx.stroke();
      ctx.restore();
    };
    this.stage.platforms.forEach((item) => draw(item.x, item.y, item.width, item.height, item.angle));
    this.stage.moving.forEach((item, index) => {
      const position = snapshot.moving[index] ?? item;
      draw(position.x, position.y, item.width, item.height, item.angle, true);
      ctx.save(); ctx.strokeStyle = withAlpha(this.stage.palette.leafDark, .22); ctx.lineWidth = .02; ctx.setLineDash([.08, .1]);
      ctx.beginPath();
      if (item.axis === 'x') { ctx.moveTo(item.x - item.distance, item.y); ctx.lineTo(item.x + item.distance, item.y); }
      else { ctx.moveTo(item.x, item.y - item.distance); ctx.lineTo(item.x, item.y + item.distance); }
      ctx.stroke(); ctx.restore();
    });
  }

  private drawPetals(ctx: CanvasRenderingContext2D, collected: ReadonlySet<number>): void {
    this.stage.petals.forEach((petal, index) => {
      if (collected.has(index)) return;
      const rotation = this.reduced ? index * .9 : this.time * .45 + index * 1.7;
      ctx.save(); ctx.translate(petal.x, petal.y); ctx.rotate(rotation);
      ctx.fillStyle = this.stage.palette.sun;
      ctx.strokeStyle = this.stage.palette.ink;
      ctx.lineWidth = .026;
      for (let i = 0; i < 4; i += 1) {
        ctx.rotate(Math.PI / 2);
        ctx.beginPath(); ctx.ellipse(0, -petal.radius * .55, petal.radius * .3, petal.radius * .62, 0, 0, TAU); ctx.fill(); ctx.stroke();
      }
      ctx.fillStyle = this.stage.palette.ink; ctx.beginPath(); ctx.arc(0, 0, .075, 0, TAU); ctx.fill();
      ctx.restore();
    });
  }

  private drawBumpers(ctx: CanvasRenderingContext2D): void {
    this.stage.bumpers.forEach((bumper, index) => {
      const breathe = this.reduced ? 1 : 1 + Math.sin(this.time * 2.8 + index) * .025;
      ctx.save(); ctx.translate(bumper.x, bumper.y); ctx.scale(breathe, breathe);
      ctx.fillStyle = withAlpha(this.stage.palette.coral, .13);
      ctx.strokeStyle = this.stage.palette.coral; ctx.lineWidth = .09;
      ctx.beginPath(); ctx.arc(0, 0, bumper.radius, 0, TAU); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = withAlpha(this.stage.palette.ink, .32); ctx.lineWidth = .02; ctx.setLineDash([.1, .12]);
      ctx.beginPath(); ctx.arc(0, 0, bumper.radius * .72, 0, TAU); ctx.stroke();
      ctx.restore();
    });
  }

  private drawThorns(ctx: CanvasRenderingContext2D): void {
    this.stage.thorns.forEach((thorn) => {
      ctx.save(); ctx.translate(thorn.x, thorn.y); ctx.rotate(thorn.angle ?? 0);
      ctx.fillStyle = this.stage.palette.coral;
      const count = Math.max(2, Math.floor(thorn.width / .34));
      ctx.beginPath(); ctx.moveTo(-thorn.width / 2, thorn.height / 2);
      for (let i = 0; i < count; i += 1) {
        const left = -thorn.width / 2 + i * thorn.width / count;
        ctx.lineTo(left + thorn.width / count * .5, -thorn.height / 2);
        ctx.lineTo(left + thorn.width / count, thorn.height / 2);
      }
      ctx.closePath(); ctx.fill();
      ctx.restore();
    });
  }

  private drawGoal(ctx: CanvasRenderingContext2D): void {
    const goal = this.stage.goal;
    ctx.save(); ctx.translate(goal.x, goal.y);
    ctx.strokeStyle = this.stage.palette.leafDark; ctx.fillStyle = withAlpha(this.stage.palette.leaf, .15); ctx.lineWidth = .045;
    for (let i = 0; i < 6; i += 1) {
      ctx.save(); ctx.rotate(i * TAU / 6 + (this.reduced ? 0 : Math.sin(this.time * .45) * .04));
      ctx.beginPath(); ctx.ellipse(0, -goal.radius * .72, goal.radius * .34, goal.radius * .58, 0, 0, TAU); ctx.fill(); ctx.stroke(); ctx.restore();
    }
    ctx.fillStyle = this.stage.palette.paperDeep; ctx.beginPath(); ctx.arc(0, 0, goal.radius * .56, 0, TAU); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = withAlpha(this.stage.palette.coral, .7); ctx.setLineDash([.08, .1]); ctx.beginPath(); ctx.arc(0, 0, goal.radius * .32, 0, TAU); ctx.stroke();
    ctx.restore();
  }

  private drawSeed(ctx: CanvasRenderingContext2D, position: Vec2, rotation: number): void {
    ctx.save(); ctx.translate(position.x, position.y); ctx.rotate(rotation + .4);
    ctx.fillStyle = this.stage.palette.ink;
    ctx.beginPath(); ctx.moveTo(0, -.33); ctx.bezierCurveTo(.3, -.2, .33, .2, 0, .34); ctx.bezierCurveTo(-.29, .19, -.3, -.19, 0, -.33); ctx.fill();
    ctx.strokeStyle = withAlpha(this.stage.palette.paper, .42); ctx.lineWidth = .025; ctx.beginPath(); ctx.moveTo(-.05, -.18); ctx.quadraticCurveTo(.08, 0, -.02, .18); ctx.stroke();
    ctx.restore();
  }

  private drawAim(ctx: CanvasRenderingContext2D, seed: Vec2, aim: AimVisual): void {
    const length = Math.max(.8, aim.strength * 2.6);
    const magnitude = Math.hypot(aim.vector.x, aim.vector.y) || 1;
    const nx = aim.vector.x / magnitude;
    const ny = aim.vector.y / magnitude;
    ctx.save();
    ctx.strokeStyle = this.stage.palette.coral; ctx.fillStyle = this.stage.palette.coral; ctx.lineWidth = .045; ctx.setLineDash([.13, .13]);
    ctx.beginPath(); ctx.moveTo(seed.x, seed.y); ctx.lineTo(seed.x + nx * length, seed.y + ny * length); ctx.stroke();
    ctx.setLineDash([]);
    for (let i = 1; i <= 5; i += 1) {
      const t = i / 5;
      const arcX = seed.x + nx * length * t;
      const arcY = seed.y + ny * length * t + 2.7 * t * t;
      ctx.globalAlpha = 1 - t * .52; ctx.beginPath(); ctx.arc(arcX, arcY, .055, 0, TAU); ctx.fill();
    }
    ctx.restore();
  }

  private drawTrail(ctx: CanvasRenderingContext2D): void {
    if (this.trail.length < 2) return;
    ctx.save(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (let i = 1; i < this.trail.length; i += 1) {
      const a = this.trail[i - 1]; const b = this.trail[i];
      if (!a || !b) continue;
      ctx.strokeStyle = withAlpha(this.stage.palette.coral, i / this.trail.length * .48);
      ctx.lineWidth = .018 + i / this.trail.length * .045;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    }
    ctx.restore();
  }

  private drawEffects(ctx: CanvasRenderingContext2D): void {
    for (const particle of this.particles) {
      if (!particle.active) continue;
      ctx.globalAlpha = Math.max(0, particle.life / particle.maxLife);
      ctx.fillStyle = particle.color;
      ctx.save(); ctx.translate(particle.x, particle.y); ctx.rotate(particle.life * 8); ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size * .45); ctx.restore();
    }
    ctx.globalAlpha = 1;
    for (const ring of this.rings) {
      const t = ring.age / ring.duration;
      ctx.strokeStyle = withAlpha(ring.color, (1 - t) * .75); ctx.lineWidth = .04 * (1 - t);
      ctx.beginPath(); ctx.arc(ring.x, ring.y, .22 + t * 1.1, 0, TAU); ctx.stroke();
    }
    ctx.font = "600 .25px 'JetBrains Mono Variable', monospace";
    ctx.textAlign = 'center';
    for (const stamp of this.stamps) {
      ctx.fillStyle = withAlpha(stamp.color, 1 - stamp.age / .85);
      ctx.fillText(stamp.text, stamp.x, stamp.y - .48 - stamp.age * .6);
    }
  }

  private drawRegistration(ctx: CanvasRenderingContext2D, width: number, height: number, dpr: number): void {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = withAlpha(this.stage.palette.ink, .42);
    ctx.font = "500 8px 'JetBrains Mono Variable', monospace";
    ctx.textAlign = 'left'; ctx.fillText(`FIELD ${String(this.stage.number).padStart(2, '0')}  /  ${this.stage.name.toUpperCase()}`, 18, height - 18);
    ctx.textAlign = 'right'; ctx.fillText('PULL · RELEASE · PULSE', width - 18, height - 18);
  }

  private updateTrail(position: Vec2): void {
    const previous = this.trail[this.trail.length - 1];
    if (!previous || Math.hypot(position.x - previous.x, position.y - previous.y) > .08) this.trail.push({ ...position });
    const limit = this.reduced ? 18 : 44;
    if (this.trail.length > limit) this.trail.splice(0, this.trail.length - limit);
  }

  private updateEffects(delta: number): void {
    this.shake *= Math.pow(.035, delta);
    for (const particle of this.particles) {
      if (!particle.active) continue;
      particle.life -= delta;
      if (particle.life <= 0) { particle.active = false; continue; }
      particle.vy += 3.4 * delta;
      particle.x += particle.vx * delta; particle.y += particle.vy * delta;
    }
    this.rings.forEach((ring) => { ring.age += delta; });
    this.rings = this.rings.filter((ring) => ring.age < ring.duration);
    this.stamps.forEach((stamp) => { stamp.age += delta; });
    this.stamps = this.stamps.filter((stamp) => stamp.age < .85);
  }

  private burst(position: Vec2, color: string, requested: number, speed: number): void {
    const count = this.reduced ? Math.ceil(requested * .32) : requested;
    let emitted = 0;
    for (const particle of this.particles) {
      if (particle.active) continue;
      const angle = emitted * 2.39996 + this.time;
      const variance = .45 + ((emitted * 37) % 71) / 100;
      particle.active = true; particle.x = position.x; particle.y = position.y;
      particle.vx = Math.cos(angle) * speed * variance; particle.vy = Math.sin(angle) * speed * variance;
      particle.maxLife = .38 + (emitted % 5) * .075; particle.life = particle.maxLife;
      particle.size = .07 + (emitted % 3) * .025; particle.color = color;
      emitted += 1;
      if (emitted >= count) break;
    }
  }

  private applyPalette(): void {
    const root = document.documentElement.style;
    root.setProperty('--paper', this.stage.palette.paper);
    root.setProperty('--paper-deep', this.stage.palette.paperDeep);
    root.setProperty('--ink', this.stage.palette.ink);
    root.setProperty('--leaf', this.stage.palette.leaf);
    root.setProperty('--leaf-dark', this.stage.palette.leafDark);
    root.setProperty('--coral', this.stage.palette.coral);
  }
}

function withAlpha(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const value = Number.parseInt(raw, 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

function easeOutCubic(value: number): number { return 1 - Math.pow(1 - value, 3); }
